import { CodeableConcept } from "../../../../types/codeableConcept";
import { generateIdentifierUuid } from "../../../../utils/generators";
import { NoteWithUuid } from "../../../../utils/noteManager";
import {
  FamilyHistory,
  FamilyHistoryConditionFormatted,
} from "../types/familyHistoryTypes";

export const conditionCodeOf = (condition?: {
  coding?: Array<{ code?: string | number }>;
}) =>
  condition?.coding?.[0]?.code != null
    ? String(condition.coding[0].code)
    : undefined;

export const sameConditionCode = (a?: unknown, b?: unknown) =>
  a != null && b != null && String(a) === String(b);

export const conditionDisplayOf = (condition?: {
  text?: string;
  coding?: Array<{ display?: string }>;
}) => (condition?.coding?.[0]?.display || condition?.text || "").trim();

export const relationshipCode = (item?: {
  relationship?: { coding?: Array<{ code?: string }> };
}) => item?.relationship?.coding?.[0]?.code;

export const membersWithRelationship = (
  items: FamilyHistory[] = [],
  code?: string,
) => items.filter((item) => relationshipCode(item) === code);

export const unionMemberConditions = (
  members: FamilyHistory[] = [],
): FamilyHistory["condition"] => {
  const byCode = new Map<string, FamilyHistory["condition"][number]>();

  members.forEach((member) => {
    (member.condition || []).forEach((condition) => {
      const code = conditionCodeOf(condition.code);
      if (!code) {
        return;
      }
      byCode.set(code, condition);
    });
  });

  return [...byCode.values()];
};

export const pickCanonicalMember = (
  members: FamilyHistory[],
): FamilyHistory | undefined => {
  if (!members.length) return undefined;

  return [...members].sort((a, b) => {
    if (Boolean(a.id) !== Boolean(b.id)) {
      return a.id ? -1 : 1;
    }
    return (b.condition?.length || 0) - (a.condition?.length || 0);
  })[0];
};

export const stripCondition = (
  item: FamilyHistory,
  conditionCode?: string,
): FamilyHistory => {
  const updatedConditions = (item.condition || []).filter(
    (c) => !sameConditionCode(conditionCodeOf(c.code), conditionCode),
  );

  return {
    ...item,
    condition: updatedConditions,
    status: updatedConditions.length === 0 ? "entered-in-error" : item.status,
  };
};

export const getFamilyHistoryItemsToDeleteCondition = ({
  originalItems = [],
  saveableItems = [],
  conditionCode,
}: {
  originalItems?: FamilyHistory[];
  saveableItems?: FamilyHistory[];
  conditionCode?: string;
}): FamilyHistory[] => {
  const byId = new Map<string, FamilyHistory>();

  saveableItems.forEach((item) => {
    if (item?.id) {
      byId.set(String(item.id), item);
    }
  });

  originalItems.forEach((item) => {
    if (!item?.id) {
      return;
    }

    const existing = byId.get(String(item.id));
    const originalHasCondition = item.condition?.some((c) =>
      sameConditionCode(conditionCodeOf(c.code), conditionCode),
    );
    const existingHasCondition = existing?.condition?.some((c) =>
      sameConditionCode(conditionCodeOf(c.code), conditionCode),
    );

    if (!existing || (originalHasCondition && !existingHasCondition)) {
      byId.set(String(item.id), item);
    }
  });

  return [...byId.values()]
    .filter((item) =>
      item.condition?.some((c) =>
        sameConditionCode(conditionCodeOf(c.code), conditionCode),
      ),
    )
    .map((item) => stripCondition(item, conditionCode));
};

const hasDeceasedValue = (item?: { deceased?: FamilyHistory["deceased"] }) =>
  Boolean(item?.deceased && (item.deceased as { value?: string }).value);

const CLEARED_DECEASED: FamilyHistory["deceased"] = {};

export const deceasedToPersist = (
  deceased?: FamilyHistory["deceased"],
): FamilyHistory["deceased"] =>
  hasDeceasedValue({ deceased }) ? deceased : CLEARED_DECEASED;

export const applyRelationshipDeceased = (
  members: FamilyHistory[] = [],
  relCode?: string,
  deceased?: FamilyHistory["deceased"],
): FamilyHistory[] => {
  if (!relCode) {
    return members;
  }

  const nextDeceased = deceasedToPersist(deceased);
  return members.map((member) =>
    relationshipCode(member) === relCode
      ? { ...member, deceased: nextDeceased }
      : member,
  );
};

export const overlayRelationshipDeceased = (
  members: FamilyHistory[] = [],
  sources: FamilyHistory[] = [],
): FamilyHistory[] =>
  members.map((member) => {
    const matches = membersWithRelationship(sources, relationshipCode(member));
    if (!matches.length) {
      return member;
    }

    const withValue = matches.find(hasDeceasedValue);
    if (withValue) {
      return { ...member, deceased: withValue.deceased };
    }

    const explicitlyCleared = matches.find(
      (item) => item.deceased != null && !hasDeceasedValue(item),
    );
    if (explicitlyCleared) {
      return {
        ...member,
        deceased: deceasedToPersist(explicitlyCleared.deceased),
      };
    }

    return member;
  });

const mergeNotes = (
  items: FamilyHistory[],
): NonNullable<FamilyHistory["note"]> => {
  const seen = new Set<string>();
  const notes: NonNullable<FamilyHistory["note"]> = [];

  items.forEach((item) => {
    (item.note || []).forEach((note) => {
      const key = note.uuid || `${note.text}|${note.time}`;
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      notes.push(note);
    });
  });

  return notes;
};

const withContributedToDeathFrom = (
  survivor: FamilyHistory,
  sources: FamilyHistory[],
) =>
  (survivor.condition || []).map((condition) => {
    if (condition.contributedToDeath) {
      return condition;
    }

    const contributed = sources.some((source) =>
      source.condition?.some(
        (other) =>
          sameConditionCode(
            conditionCodeOf(other.code),
            conditionCodeOf(condition.code),
          ) && other.contributedToDeath,
      ),
    );

    return contributed ? { ...condition, contributedToDeath: true } : condition;
  });

export const mergeDuplicateMemberDetails = (
  survivor: FamilyHistory,
  sources: FamilyHistory[],
): FamilyHistory => {
  const deceasedExplicitlySet = survivor.deceased != null;
  const deceased =
    hasDeceasedValue(survivor) || deceasedExplicitlySet
      ? survivor.deceased
      : (sources.find(hasDeceasedValue)?.deceased ?? survivor.deceased);
  const note = mergeNotes([survivor, ...sources]);

  return {
    ...survivor,
    deceased,
    note: note.length ? note : survivor.note,
    condition: withContributedToDeathFrom(survivor, sources),
  };
};

export const formatFamilyHistoryItems = (
  items: any[],
): FamilyHistoryConditionFormatted[] => {
  const conditionMap = new Map<string, FamilyHistoryConditionFormatted>();

  items.forEach((familyMember) => {
    if (familyMember.status === "entered-in-error") {
      return;
    }

    familyMember.condition?.forEach((condition) => {
      const conditionKey = conditionCodeOf(condition.code);
      if (!conditionKey) {
        return;
      }

      if (!conditionMap.has(conditionKey)) {
        conditionMap.set(conditionKey, {
          ids: [],
          status: familyMember.status || "partial",
          condition: condition.code,
          notes: [],
          relationships: [],
        });
      }

      const currentGroup = conditionMap.get(conditionKey);
      if (currentGroup && familyMember.id) {
        if (!currentGroup.ids.includes(familyMember.id)) {
          currentGroup.ids.push(familyMember.id);
        }
      }

      if (familyMember.note && Array.isArray(familyMember.note)) {
        const convertedNotes: NoteWithUuid[] = familyMember.note.map(
          (annotation: any) => ({
            uuid: annotation.uuid || generateIdentifierUuid(),
            author: annotation.author?.authorReference || annotation.author,
            text: annotation.text || "",
            time: new Date(annotation.time) || new Date(),
          }),
        );

        if (currentGroup) {
          const existingUuids = new Set(
            currentGroup.notes.map((note) => note.uuid),
          );
          const uniqueNewNotes = convertedNotes.filter(
            (note) => !existingUuids.has(note.uuid),
          );
          currentGroup.notes = [...currentGroup.notes, ...uniqueNewNotes];
        }
      }

      const relCode = relationshipCode(familyMember);
      const existingRelationship = currentGroup?.relationships.find(
        (rel) => relationshipCode(rel) === relCode,
      );

      if (existingRelationship) {
        if (familyMember.id) {
          const nextIds = new Set(
            [
              ...(existingRelationship.ids || []),
              existingRelationship.id,
              familyMember.id,
            ].filter(Boolean),
          );
          existingRelationship.ids = [...nextIds];
          if (!existingRelationship.id) {
            existingRelationship.id = familyMember.id;
          }
        }
        if (
          familyMember.deceased != null &&
          existingRelationship.deceased !== familyMember.deceased
        ) {
          existingRelationship.deceased = familyMember.deceased;
        }
        return;
      }

      currentGroup?.relationships.push({
        id: familyMember.id,
        ids: familyMember.id ? [familyMember.id] : [],
        relationship: familyMember.relationship,
        sex: familyMember.sex,
        deceased: familyMember.deceased,
        onset: condition.onset,
        contributedToDeath: condition.contributedToDeath,
      });
    });
  });

  return Array.from(conditionMap.values()).sort((a, b) => {
    const aLabel = conditionDisplayOf(a.condition);
    const bLabel = conditionDisplayOf(b.condition);

    if (!aLabel && !bLabel) {
      return 0;
    }
    if (!aLabel) {
      return 1;
    }
    if (!bLabel) {
      return -1;
    }

    return aLabel.localeCompare(bLabel, undefined, {
      numeric: true,
      sensitivity: "base",
    });
  });
};

export const applyRemovedFamilyHistoryRelationship = (
  saveableItems: FamilyHistory[],
  removedCode: string,
  conditionCode?: string,
): FamilyHistory[] =>
  saveableItems.flatMap((item) => {
    if (relationshipCode(item) !== removedCode) {
      return [item];
    }

    if (!item.id) {
      return [];
    }

    return [stripCondition(item, conditionCode)];
  });

export const buildSaveableFamilyHistoryItems = ({
  relationships,
  condition,
  originalItems,
  existingItems,
  annotationNotes,
}: {
  relationships?: FamilyHistoryConditionFormatted["relationships"];
  condition?: CodeableConcept;
  originalItems?: FamilyHistory[];
  existingItems?: FamilyHistory[];
  annotationNotes?: FamilyHistory["note"];
}): FamilyHistory[] | undefined => {
  if (!relationships?.length || !conditionCodeOf(condition)) {
    return undefined;
  }

  return relationships.map((rel) => {
    const code = relationshipCode(rel);
    const candidates = [
      ...membersWithRelationship(originalItems, code),
      ...membersWithRelationship(existingItems, code),
    ];
    const canonical = pickCanonicalMember(candidates);

    if (canonical) {
      const merged = mergeDuplicateMemberDetails(canonical, candidates);
      const currentCondition = {
        code: { ...condition },
        onset: rel.onset,
        contributedToDeath: rel.contributedToDeath,
      };

      return {
        ...merged,
        relationship: rel.relationship,
        sex: rel.sex ?? canonical.sex,
        deceased: deceasedToPersist(rel.deceased),
        status:
          canonical.status === "entered-in-error"
            ? "partial"
            : canonical.status || "partial",
        condition: [
          ...unionMemberConditions(canonical.id ? candidates : []).filter(
            (c) =>
              !sameConditionCode(
                conditionCodeOf(c.code),
                conditionCodeOf(condition),
              ),
          ),
          currentCondition,
        ],
        note: annotationNotes ?? merged.note,
      };
    }

    return {
      status: "partial",
      relationship: rel.relationship,
      sex: rel.sex,
      deceased: deceasedToPersist(rel.deceased),
      condition: [
        {
          code: condition,
          onset: rel.onset,
          contributedToDeath: rel.contributedToDeath,
        },
      ],
      ...(annotationNotes && { note: annotationNotes }),
    };
  });
};

export const getFamilyHistoryItemsToPersist = ({
  saveableItems,
  originalItems,
  currentRelationships,
  conditionCode,
  stripUnselectedRelationships = true,
}: {
  saveableItems: FamilyHistory[];
  originalItems: FamilyHistory[];
  currentRelationships: FamilyHistoryConditionFormatted["relationships"];
  conditionCode?: string;
  stripUnselectedRelationships?: boolean;
}): FamilyHistory[] => {
  const currentCodes = new Set(
    (currentRelationships || [])
      .map((rel) => relationshipCode(rel))
      .filter(Boolean),
  );

  const deceasedByRel = new Map(
    (currentRelationships || [])
      .map((rel) => {
        const code = relationshipCode(rel);
        return code
          ? ([code, deceasedToPersist(rel.deceased)] as const)
          : undefined;
      })
      .filter(Boolean) as Array<readonly [string, FamilyHistory["deceased"]]>,
  );

  const selectedByRel = new Map<string, FamilyHistory>();
  saveableItems.forEach((item) => {
    const code = relationshipCode(item);
    if (!code || !currentCodes.has(code)) {
      return;
    }
    const existing = selectedByRel.get(code);
    selectedByRel.set(
      code,
      pickCanonicalMember([existing, item].filter(Boolean) as FamilyHistory[]),
    );
  });

  const persist: FamilyHistory[] = [...selectedByRel.values()].map((item) => {
    const code = relationshipCode(item);
    const sources = [
      ...membersWithRelationship(originalItems, code),
      ...membersWithRelationship(saveableItems, code),
      item,
    ];

    return {
      ...item,
      condition: unionMemberConditions(sources),
      deceased: deceasedByRel.get(code) ?? deceasedToPersist(item.deceased),
    };
  });

  originalItems.forEach((item) => {
    if (!item.id) {
      return;
    }

    const code = relationshipCode(item);
    const hasThisCondition = item.condition?.some((c) =>
      sameConditionCode(conditionCodeOf(c.code), conditionCode),
    );
    const canonical = selectedByRel.get(code);

    if (currentCodes.has(code)) {
      if (canonical?.id && canonical.id !== item.id) {
        const updated = hasThisCondition
          ? stripCondition(item, conditionCode)
          : { ...item };
        persist.push({
          ...updated,
          deceased:
            deceasedByRel.get(code) ?? deceasedToPersist(canonical.deceased),
        });
      }
      return;
    }

    if (!hasThisCondition) {
      return;
    }

    if (!stripUnselectedRelationships) {
      return;
    }

    persist.push(stripCondition(item, conditionCode));
  });

  const seenIds = new Set<string>();
  const deduped = persist.filter((item) => {
    const code = relationshipCode(item);
    if (!item.id) {
      return currentCodes.has(code);
    }
    if (seenIds.has(item.id)) {
      return false;
    }
    seenIds.add(item.id);
    return true;
  });

  return deduped.map((item) => {
    if (!item.id || item.status === "entered-in-error") {
      return item;
    }

    const code = relationshipCode(item);
    const sources = [
      ...membersWithRelationship(originalItems, code),
      ...membersWithRelationship(deduped, code),
    ];
    const merged = mergeDuplicateMemberDetails(item, sources);

    if (currentCodes.has(code)) {
      return {
        ...merged,
        deceased:
          deceasedByRel.get(code) ??
          deceasedToPersist(selectedByRel.get(code)?.deceased),
      };
    }

    return merged;
  });
};

export const mergeFamilyHistoryMembers = (
  existing: FamilyHistory[] = [],
  saved: FamilyHistory[] = [],
): FamilyHistory[] => {
  const merged = new Map(
    existing
      .filter((member) => member?.id)
      .map((member) => [String(member.id), member]),
  );

  saved.forEach((member) => {
    if (!member?.id) {
      return;
    }
    merged.set(String(member.id), member);
  });

  return Array.from(merged.values()).filter(
    (member) => member.status !== "entered-in-error",
  );
};

export const refreshFamilyHistoryCards = (
  existing: FamilyHistory[] = [],
  saved: FamilyHistory[] = [],
) => formatFamilyHistoryItems(mergeFamilyHistoryMembers(existing, saved));

export const retainUnsavedFamilyCards = (
  currentCards: Array<{ ids?: string[] }> = [],
  nextCards: Array<{ ids?: string[] }> = [],
  excludeIndex?: number,
) => [
  ...nextCards,
  ...currentCards.filter(
    (card, index) => !card?.ids?.length && index !== excludeIndex,
  ),
];

export const familyHistoryCardKey = (
  card?: {
    ids?: string[];
    clientKey?: string;
    condition?: { coding?: Array<{ code?: string | number }> };
  },
  index = 0,
) =>
  conditionCodeOf(card?.condition) ||
  card?.clientKey ||
  card?.ids?.[0] ||
  `new-${index}`;
